import { motion } from 'framer-motion';
import { CheckCircle2, Target, Rocket, Award } from 'lucide-react';
import { BRAND, MENTOR_TIMELINE } from '@/data/mockData';
import ParticlesBg from '@/components/common/ParticlesBg';

const About = () => {
  return (
    <>
      {/* Mission Hero */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 radial-glow" />
        <ParticlesBg count={40} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase text-brand-cyan/90 font-mono">About BYMAT Automation</div>
          <h1 className="font-display mt-3 text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight max-w-5xl leading-[1.05] font-semibold">
            Our mission is to transform <span className="text-brand-cyan">1 million IT professionals</span> with industry-ready Automation Testing and AI skills.
          </h1>
          <p className="mt-6 text-white/70 max-w-2xl leading-relaxed">
            Founded in {BRAND.founded}, BYMAT Automation is committed to reshaping QA careers through real-time training, AI-augmented automation, and project-based mentorship aligned to current IT industry demand.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Target, t: 'Real-Time Industry Training', d: 'Curriculum mapped to what hiring managers test today.' },
            { icon: Rocket, t: 'AI-Powered Automation', d: 'GenAI workflows for test generation & self-healing.' },
            { icon: Award, t: 'Career Transformation', d: 'Resume rebuild, mock interviews, and referral network.' },
          ].map((p, i) => (
            <motion.div key={p.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="glass rounded-2xl p-7">
              <div className="h-11 w-11 rounded-lg bg-brand-cyan/10 text-brand-cyan flex items-center justify-center border border-brand-cyan/20">
                <p.icon className="w-5 h-5" />
              </div>
              <h3 className="mt-5 font-display font-bold text-lg">{p.t}</h3>
              <p className="mt-2 text-sm text-white/65 leading-relaxed">{p.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Mentor Authority */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-brand-cyan/30 to-blue-600/20 blur-2xl" />
              <img
                src="https://customer-assets.emergentagent.com/job_bymat-lms-portal/artifacts/vfuxp02z_1.jpg"
                alt="Rahul Ganjare"
                className="relative w-full aspect-[4/5] object-cover rounded-3xl border border-white/10"
              />
              <div className="absolute bottom-5 left-5 glass-strong rounded-xl px-5 py-3">
                <div className="font-display text-lg font-bold">Rahul Ganjare</div>
                <div className="text-[10px] tracking-widest uppercase text-brand-cyan font-mono">Founder & Lead Mentor</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="text-xs tracking-[0.3em] uppercase text-brand-cyan/90 font-mono">Meet the Mentor</div>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight font-semibold">
              18+ years of QA. Architect, Educator, Mentor.
            </h2>
            <p className="mt-5 text-white/70 leading-relaxed">
              Rahul Ganjare has architected enterprise automation frameworks for global banking, fintech, and e-commerce clients. As founder of BYMAT, he combines hands-on engineering with structured mentorship to deliver verifiable career outcomes.
            </p>

            <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {['Java', 'Selenium', 'Cucumber BDD', 'Playwright', 'Appium', 'DevOps', 'AI Automation', 'Architecture', 'Mentorship'].map((t) => (
                <div key={t} className="glass rounded-lg px-3 py-2 text-sm font-medium flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-brand-cyan" /> {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase text-brand-cyan/90 font-mono text-center">The Journey</div>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl font-black tracking-tight text-center font-semibold">From manual tester to industry mentor</h2>

          <div className="relative mt-12">
            <div className="absolute left-4 sm:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-brand-cyan/40 to-transparent -translate-x-px" />
            <div className="space-y-10">
              {MENTOR_TIMELINE.map((m, i) => (
                <div key={m.year} className={`relative flex flex-col sm:flex-row gap-6 ${i % 2 === 0 ? 'sm:flex-row' : 'sm:flex-row-reverse'}`}>
                  <div className="sm:w-1/2 sm:px-8">
                    <motion.div
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className="glass rounded-2xl p-5"
                    >
                      <div className="font-mono text-xs tracking-widest text-brand-cyan/90">{m.year}</div>
                      <h3 className="mt-2 font-display font-bold text-lg">{m.title}</h3>
                      <p className="mt-2 text-sm text-white/65">{m.desc}</p>
                    </motion.div>
                  </div>
                  <div className="absolute left-4 sm:left-1/2 top-5 -translate-x-1/2 w-3 h-3 rounded-full bg-brand-cyan shadow-[0_0_15px_rgba(0,240,255,0.8)]" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
